import {
  formatDay,
  isRealDay,
  shiftDay,
  warsawToday,
  type CalorieDayDto,
  type CalorieDaySectionDto,
  type CalorieEntryDto,
  type CalorieMealDto,
  type CalorieSettingsDto,
} from '@iac/contracts';
import { useId, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { Badge } from '../../components/Badge';
import { DeleteButton } from '../../components/DeleteButton';
import { DonutChart } from '../../components/DonutChart';
import {
  IconArrowsRightLeft,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconPencilSquare,
  IconPlus,
  IconTrash,
} from '../../components/icons';
import { Menu } from '../../components/Menu';
import { PageHeader } from '../../components/PageHeader';
import { Panel } from '../../components/Panel';
import { Stat } from '../../components/Stat';
import { useDict } from '../../i18n/dictionary';
import { usePageTitle } from '../../lib/page';
import {
  useCalorieSettings,
  useCreateEntry,
  useDay,
  useDeleteEntry,
  useMoveEntry,
  useStepEntry,
  useUpdateEntry,
} from './api';
import { EntryForm } from './EntryForm';
import { SwipeRow } from './SwipeRow';
import { caloriesDict } from './i18n';
import { dayHeading, dayHref, entryDetail } from './links';
import { useLocale } from '../../lib/locale';

/** "+ 2 bez kcal" beside a total that leaves those entries out. */
function IncompleteNote({ count }: { count: number }) {
  const t = useDict(caloriesDict);
  if (count === 0) return null;
  return <span className="kcal-incomplete">{t.incomplete(count)}</span>;
}

/**
 * The move button's dropdown: every other section of the day. Closes on a
 * pick, Escape or a press outside it. Hidden when there is nowhere to go.
 */
function MoveMenu({
  entry,
  meals,
}: {
  entry: CalorieEntryDto;
  meals: CalorieMealDto[];
}) {
  const t = useDict(caloriesDict);
  const move = useMoveEntry();

  // "Bez sekcji" is not a target: only the catalog logs there.
  const targets = meals.filter((meal) => meal.id !== entry.mealId);
  if (targets.length === 0) return null;

  return (
    <Menu
      label={t.moveTo(entry.name)}
      triggerLabel={t.moveItem(entry.name)}
      title={t.move}
      icon={<IconArrowsRightLeft />}
      items={targets.map((target) => ({
        key: target.id,
        label: target.name,
        onSelect: () =>
          move.mutate({ id: entry.id, date: entry.date, mealId: target.id }),
      }))}
    />
  );
}

/**
 * One product of the day, as its own card. Desktop: icons for move, edit and
 * delete. Phone: the icons hide; a tap edits, a right swipe adds one more and
 * a left swipe deletes.
 */
function EntryRow({
  entry,
  meals,
}: {
  entry: CalorieEntryDto;
  meals: CalorieMealDto[];
}) {
  const t = useDict(caloriesDict);
  const [editing, setEditing] = useState(false);
  const update = useUpdateEntry();
  const step = useStepEntry();
  const remove = useDeleteEntry();

  const confirmDelete = t.confirmDeleteEntry(entry.name);

  if (editing) {
    return (
      <li className="entry-card entry-card-form">
        <EntryForm
          entry={entry}
          meals={meals}
          submitLabel={t.save}
          pending={update.isPending}
          onSubmit={(values) =>
            update.mutate(
              { id: entry.id, ...values },
              { onSuccess: () => setEditing(false) },
            )
          }
          onCancel={() => setEditing(false)}
        />
      </li>
    );
  }

  return (
    <SwipeRow
      // Clipped only mid-swipe; otherwise the move menu has to overflow.
      className="entry-card"
      swipingClassName="entry-card-swiping"
      bodyClassName="entry-card-body"
      startLabel="+1"
      deleteLabel={
        <>
          {t.delete} <IconTrash />
        </>
      }
      onRight={() => step.mutate({ id: entry.id, by: 1 })}
      onLeft={() => {
        if (window.confirm(confirmDelete)) remove.mutate(entry.id);
      }}
      onClick={() => setEditing(true)}
    >
      <div className="entry-main">
        <p className="entry-name">{entry.name}</p>
        <p className="entry-detail">{entryDetail(entry)}</p>
      </div>
      {entry.kcal === null ? (
        <Badge tone="warning">{t.noKcal}</Badge>
      ) : (
        <p className="entry-card-kcal">
          {entry.kcal} <span className="entry-card-kcal-unit">kcal</span>
        </p>
      )}
      {/* Desktop only. Clicks here mustn't also open the editor. */}
      <div className="entry-card-actions" onClick={(e) => e.stopPropagation()}>
        <MoveMenu entry={entry} meals={meals} />
        <button
          type="button"
          className="btn-icon-ghost"
          aria-label={t.editItem(entry.name)}
          title={t.edit}
          onClick={() => setEditing(true)}
        >
          <IconPencilSquare />
        </button>
        <DeleteButton
          label={t.deleteItem(entry.name)}
          confirm={confirmDelete}
          onConfirm={() => remove.mutate(entry.id)}
        />
      </div>
    </SwipeRow>
  );
}

function SectionCard({
  section,
  date,
  meals,
}: {
  section: CalorieDaySectionDto;
  date: string;
  meals: CalorieMealDto[];
}) {
  const bodyId = useId();
  const t = useDict(caloriesDict);
  const [adding, setAdding] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const create = useCreateEntry();
  const title = section.meal?.name ?? t.noMeal;

  return (
    <section className="meal-section">
      {/* A click anywhere on the header folds the section away; its sum stays
          in sight. The name is the button, for the keyboard and screen
          readers — its click bubbles here. */}
      <div
        className="meal-header"
        onClick={() => setCollapsed((current) => !current)}
      >
        <h2 className="meal-title">
          <button
            type="button"
            className="meal-toggle"
            aria-expanded={!collapsed}
            aria-controls={bodyId}
          >
            <span
              className={
                collapsed ? 'meal-toggle-icon is-collapsed' : 'meal-toggle-icon'
              }
            >
              <IconChevronDown />
            </span>
            {title}
          </button>
        </h2>
        <p className="meal-kcal">
          {section.kcal} kcal <IncompleteNote count={section.incompleteCount} />
        </p>
        {/* "Bez sekcji" takes new products only while there are no sections;
            after that they come to it from the catalog alone. */}
        {!adding && (section.meal !== null || meals.length === 0) && (
          <button
            type="button"
            className="btn-primary-icon meal-add-button"
            aria-label={t.addProductTo(title)}
            title={t.addProduct}
            onClick={(e) => {
              e.stopPropagation();
              setCollapsed(false);
              setAdding(true);
            }}
          >
            <IconPlus />
          </button>
        )}
      </div>

      <div id={bodyId} hidden={collapsed}>
        {adding && (
          <div className="meal-add">
            {/* Stays open after a save, for the next product of the meal. */}
            <EntryForm
              submitLabel={t.add}
              pending={create.isPending}
              onSubmit={(values, reset) =>
                create.mutate(
                  { ...values, date, mealId: section.meal?.id ?? null },
                  { onSuccess: reset },
                )
              }
              onCancel={() => setAdding(false)}
            />
          </div>
        )}

        {section.entries.length > 0 ? (
          <ul className="entry-cards">
            {section.entries.map((entry) => (
              <EntryRow key={entry.id} entry={entry} meals={meals} />
            ))}
          </ul>
        ) : (
          !adding && <p className="meal-empty">{t.nothingAdded}</p>
        )}

        {section.meal === null && meals.length === 0 && (
          <p className="meal-hint">
            {t.mealHintBefore}
            <Link to="/calories/settings" className="text-link">
              {t.mealHintLink}
            </Link>
            {t.mealHintAfter}
          </p>
        )}
      </div>
    </section>
  );
}

/**
 * The day against the limit, as the user's settings say: two cards, or one
 * ring. Past the limit the ring splits into the limit and the excess, so the
 * slices still add up to what was eaten. The hole holds what is left of the
 * limit — negative and red once it is gone.
 */
function DaySummary({
  day,
  settings,
}: {
  day: CalorieDayDto;
  settings: CalorieSettingsDto;
}) {
  const t = useDict(caloriesDict);
  const limit = settings.dailyLimitKcal;
  const left = limit - day.kcal;

  if (settings.dayView === 'cards') {
    return (
      <div className="day-stats">
        <Stat label={t.eaten} value={`${day.kcal} kcal`}>
          <IncompleteNote count={day.incompleteCount} />
        </Stat>
        {left < 0 ? (
          <Stat label={t.over} value={`${-left} kcal`} tone="danger" />
        ) : (
          <Stat label={t.left} value={`${left} kcal`} />
        )}
      </div>
    );
  }

  return (
    <Panel as="div" className="day-chart">
      <DonutChart
        label={t.dayChart}
        slices={
          left < 0
            ? [
                { label: t.limit, value: limit },
                { label: t.over, value: -left, tone: 'danger' },
              ]
            : [
                { label: t.eaten, value: day.kcal },
                { label: t.left, value: left },
              ]
        }
        center={
          <span className="day-chart-center">
            <span
              className={left < 0 ? 'day-chart-center-danger' : undefined}
            >{`${left} kcal`}</span>
            <span className="day-chart-center-caption">
              {left < 0 ? t.overLimit : t.left}
            </span>
          </span>
        }
        formatValue={(value) => `${value} kcal`}
      />
      <IncompleteNote count={day.incompleteCount} />
    </Panel>
  );
}

/** `/calories` is today; `/calories/days/:date` is any other day. */
export function CaloriesDay() {
  const params = useParams<{ date: string }>();
  const today = warsawToday();
  const date = params.date ?? today;
  if (!isRealDay(date)) return <Navigate to="/calories" replace />;
  return <DayView date={date} today={today} />;
}

function DayView({ date, today }: { date: string; today: string }) {
  const { locale } = useLocale();
  const t = useDict(caloriesDict);
  const navigate = useNavigate();
  usePageTitle(
    `${date === today ? t.today : formatDay(date, locale)} — ${t.titleSuffix}`,
  );
  const { data: day } = useDay(date);
  const { data: settings } = useCalorieSettings();
  const meals =
    day?.sections.flatMap((s) => (s.meal === null ? [] : [s.meal])) ?? [];

  return (
    <>
      <PageHeader
        title={dayHeading(date, today, locale, t)}
        className="day-heading-row"
        actions={
          <div className="day-nav">
            <button
              type="button"
              className="btn-icon-ghost"
              aria-label={t.previousDay}
              onClick={() => void navigate(dayHref(shiftDay(date, -1), today))}
            >
              <IconChevronLeft />
            </button>
            <input
              type="date"
              aria-label={t.goToDay}
              className="field-input"
              value={date}
              onChange={(e) => {
                if (isRealDay(e.target.value))
                  void navigate(dayHref(e.target.value, today));
              }}
            />
            <button
              type="button"
              className="btn-icon-ghost"
              aria-label={t.nextDay}
              onClick={() => void navigate(dayHref(shiftDay(date, 1), today))}
            >
              <IconChevronRight />
            </button>
          </div>
        }
      />

      {/* Neither view until the settings say which, so one never flashes
          before the other. */}
      {day && settings && <DaySummary day={day} settings={settings} />}

      {/* Keyed by day so switching days closes any open form. "Bez sekcji"
          only when it holds something, or when there are no sections to add
          to instead. */}
      {day?.sections
        .filter(
          (section) =>
            section.meal !== null ||
            section.entries.length > 0 ||
            meals.length === 0,
        )
        .map((section) => (
          <SectionCard
            key={`${date}-${section.meal?.id ?? 'none'}`}
            section={section}
            date={date}
            meals={meals}
          />
        ))}
    </>
  );
}
